import React, { useState, useEffect, useContext } from 'react';
import { Button, Col, Form, Modal, ModalFooter, Row } from 'react-bootstrap';
import 'react-date-picker/dist/DatePicker.css';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { api } from '../../providers/apiClient';
import SubmitButton from '../form/submit-button';
import Instructions from './instructions';
import StatusResponse from './StatusResponse';
import enumQuestionResponseStatus from '../../enum/enumQuestionResponseStatus';
import { ContextUser } from '../../providers/ContextUser';
import { complianceStatus } from '../../providers/compliance-status';
import { toUpperCaseClean, formatDateToUTC } from '../../utils';
import {
  StyledModal,
  StyledModalHeader,
  StyledModalBody,
  StyledDescription,
  StyledContainer,
  StyledRiskFactor,
  StyledFormLabel,
  StyledResponseContainer,
  StyledStrong,
  StyledModalFooter,
  StyledServiceDeskModal,
} from './styles.js';

const ModalQuestion = ({
  item,
  isModalOpened,
  handleToggleModal,
  fetchQuestions,
}) => {
  const { currentUser, currentOrganization } = useContext(ContextUser);
  const [requesting, setRequesting] = useState(false);
  const [riskMatrix, setRiskMatrix] = useState(null);
  const [riskMatrixDesc, setRiskMatrixDesc] = useState(null);
  const [showModalData, setShowModalData] = useState(null);
  const [showModalServiceDeskOpen, setShowModalServiceDeskOpen] =
    useState(false);
  const [serviceDeskSugestionDesc, setServiceDeskSugestionDesc] = useState('');

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    control,
    formState: { errors },
  } = useForm({
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });

  const sendNewServiceDesk = async (modalData, description) => {
    const { name: userName, phone: userPhone } = currentUser;
    const { name: orgName } = currentOrganization;
    const { question_details, unit, branch, department } = item;

    const serviceDeskPayload = {
      orgId: currentOrganization.id,
      branchId: branch?.id || null,
      unitId: unit?.id || null,
      departmentId: department?.id || null,
      usuarioId: currentUser.id,
      telefone: `(${userPhone.ddd})${userPhone.number}`,
      resumo: question_details.short_description,
      descricao: description,
      observacao: modalData.observation,
      prioridade: 'MEDIA', // Prioridade padrão para chamados automáticos
      categoria: 'COMPLIANCE', // Categoria padrão para chamados de compliance
    };

    try {
      await api.post(`/service_desk`, serviceDeskPayload);
      toast.success(`Chamado criado com sucesso!`);
    } catch (error) {
      console.error('Erro ao criar chamado:', error);
      toast.error('Erro ao registrar novo chamado.');
    }
  };

  const sendResponse = async (data) => {
    const responseData = {
      impact: +data.impact,
      probability: +data.probability,
      response: +data.response,
      observation: data.observation,
    };
    
    try {
      const response = await api.patch(
        `questions/rel_questions/${data.id}/send_response`,
        responseData
      );
      toast.success(
        `Pergunta respondida! Data de expiração: ${response.data.expiresAt}`
      );
      handleToggleModal();
      fetchQuestions();
    } catch (error) {
      toast.error('Erro ao responder pergunta.');
    } finally {
      setRequesting(false);
    }
  };

  useEffect(() => {
    reset(item);
    setValue('response', item?.response?.id || null);
    setValue('probability', item?.probability || 0);
    setValue('impact', item?.impact || 0);
  }, [item]);

  const onSubmit = handleSubmit(async (data) => {
    setShowModalData(data);
    setServiceDeskSugestionDesc(
      item?.question_details?.detail_description || ''
    );
    const isCompliance = data.response === enumQuestionResponseStatus.YES;

    if (!isCompliance) {
      setShowModalServiceDeskOpen(true);
    } else {
      setRequesting(true);
      await sendResponse(data);
    }
  });

  const handleServiceDeskModal = async (
    shouldRegisterServiceDesk,
    finalDescription
  ) => {
    setShowModalServiceDeskOpen(false);
    setRequesting(true);
    if (shouldRegisterServiceDesk) {
      await sendNewServiceDesk(showModalData, finalDescription);
    }
    await sendResponse(showModalData);
  };

  const probabilityList = [
    {
      id: 1,
      desc: 'Baixa: Improvável, ocorrendo apenas em circunstâncias excepcionais.',
    },
    {
      id: 2,
      desc: 'Moderada: Possível, podendo ocorrer ocasionalmente sob certas condições.',
    },
    {
      id: 3,
      desc: 'Alta: Provável, com uma tendência de ocorrer regularmente ou frequentemente.',
    },
  ];

  const impactsList = [
    {
      id: 1,
      desc: 'Baixo: Efeitos perceptíveis, porém limitados e de fácil resolução.',
    },
    {
      id: 2,
      desc: 'Moderado: Efeitos notáveis, causando problemas ou exigindo esforço para resolução.',
    },
    {
      id: 3,
      desc: 'Alto: Efeitos graves ou severos, causando danos e exigindo recursos ou resposta urgente.',
    },
  ];

  const calculateRiskMatrixDesc = () => {
    if (riskMatrix) {
      let riskMatrixDesc = '';

      if (riskMatrix > 0 && riskMatrix <= 3) {
        riskMatrixDesc = 'Baixo';
      } else if (riskMatrix >= 4 && riskMatrix <= 6) {
        riskMatrixDesc = 'Médio';
      } else if (riskMatrix > 7) {
        riskMatrixDesc = 'Alto';
      }

      setRiskMatrixDesc(riskMatrixDesc);
    }
  };

  const calculateMatrix = () => {
    setRiskMatrix(watch('probability') * watch('impact'));
  };

  useEffect(() => {
    calculateMatrix();
  }, [watch('probability'), watch('impact')]);

  useEffect(() => {
    calculateRiskMatrixDesc();
  }, [riskMatrix]);

  const ModalServiceDesk = ({ modalData, serviceDeskSugestionDesc }) => {
    const response = complianceStatus[modalData?.response];
    const [finalDescription, setFinalDescription] = useState(
      serviceDeskSugestionDesc
    );

    return (
      <StyledServiceDeskModal
        id='modal-service-desk'
        show={showModalServiceDeskOpen}
        centered
        onHide={() => handleServiceDeskModal(false)}
        size='lg'
      >
        <StyledModalHeader id='modal-service-desk-header' closeButton>Novo chamado!</StyledModalHeader>
        <StyledModalBody id='modal-service-desk-body'>
          <StyledDescription id='modal-service-desk-description'>
            Identificamos que a situação atual dessa questão está como (
            <StyledStrong color={response?.color}>
              {response?.label}
            </StyledStrong>
            ). É recomendado informar o responsável para que as ações corretivas
            necessárias possam ser implementadas.
            <br />
            <br />
            Deseja registrar um novo chamado para o responsável?
          </StyledDescription>
          <Form.Group id='modal-service-desk-description-group'>
            <Form.Label id='modal-service-desk-description-label'>Descrição sugerida:</Form.Label>
            <Form.Control
              id='modal-service-desk-description-textarea'
              as='textarea'
              rows={4}
              value={finalDescription}
              onChange={(e) => setFinalDescription(e.target.value)}
              style={{caretColor: 'auto'}}
            />
          </Form.Group>
        </StyledModalBody>
        <StyledModalFooter id='modal-service-desk-footer' className='justify-content-between'>
          <Button
            id='modal-service-desk-no-button'
            type='button'
            variant='light'
            onClick={() => handleServiceDeskModal(false, finalDescription)}
          >
            Não
          </Button>
          <Button
            id='modal-service-desk-yes-button'
            type='button'
            onClick={() => handleServiceDeskModal(true, finalDescription)}
            loading={false}
            block={false}
          >
            Sim, Registrar chamado!
          </Button>
        </StyledModalFooter>
      </StyledServiceDeskModal>
    );
  };

  return (
    <>
      <StyledModal
          id='modal-question'
          show={isModalOpened}
          centered
          onHide={handleToggleModal}
          size='xl'
          showModalServiceDeskOpen={showModalServiceDeskOpen}
        >
          <form id='modal-question-form' onSubmit={onSubmit}>
            <StyledModalHeader id='modal-question-header' closeButton>
              {item?.question_details.short_description}
            </StyledModalHeader>
            <StyledModalBody id='modal-question-body'>
              <StyledDescription id='modal-question-description'>
                {item?.question_details.detail_description}
              </StyledDescription>
            <Instructions
              activityDescription={item?.question_details?.activity_description}
              actionHelp={item?.question_details?.action_help}
            />
            <StyledResponseContainer id='modal-question-response-container'>
              <Col md={9} id='modal-question-response-col'>
                <Controller
                  control={control}
                  name='response'
                  rules={{
                    required: 'A resposta de compliance para a questão deve ser selecionada',
                    min: {
                      value: 2,
                      message: 'A resposta de compliance para a questão deve ser selecionada'
                    },
                  }}
                  render={({ field: { value, onChange } }) => (
                    <StatusResponse
                      id='modal-question-status-response'
                      value={value}
                      onChange={onChange}
                      errors={errors}
                    />
                  )}
                />
              </Col>
              <Col md={3} id='modal-question-expiration-col'>
                <Form.Group id='modal-question-expiration-group'>
                  <StyledFormLabel id='modal-question-expiration-label'>Data de Expiração</StyledFormLabel>
                  <StyledContainer id='modal-question-expiration-date'>{item.expiresAt}</StyledContainer>
                </Form.Group>
              </Col>
            </StyledResponseContainer>
            <Form.Group id='modal-question-observation-group'>
              <Form.Label id='modal-question-observation-label'>Observações</Form.Label>
              <Form.Control
                id='modal-question-observation-textarea'
                style={{ caretColor: 'auto' }}
                as='textarea'
                {...register('observation', {
                  required: {
                    value: watch('response') === enumQuestionResponseStatus.PARTIAL ||
                           watch('response') === enumQuestionResponseStatus.NO,
                    message: 'As observações são obrigatórias quando a resposta é Parcial Compliance ou Não Compliance'
                  }
                })}
                isInvalid={!!errors.observation}
              />
              <Form.Control.Feedback type="invalid" id='modal-question-observation-error'>
                {errors.observation?.message}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group id='modal-question-risk-section'>
              <StyledRiskFactor id='modal-question-risk-factor'>
                <Row id='modal-question-risk-row'>
                  <Col md={12}>
                    <Form.Group id='modal-question-probability-group'>
                      <Form.Label id='modal-question-probability-label'>Probabilidade</Form.Label>
                      <Form.Select
                        id='modal-question-probability-select'
                        {...register('probability', {
                          required: 'A probabilidade de ocorrência para essa questão deve ser selecionada',
                          valueAsNumber: true,
                          validate: (value) => {
                            if (!value || value === 0) {
                              return 'A probabilidade de ocorrência para essa questão deve ser selecionada';
                            }
                            return true;
                          }
                        })}
                        isInvalid={!!errors.probability}
                      >
                        <option value={0}>Selecione a probabilidade de ocorrencia</option>
                        {probabilityList.map((i) => (
                          <option key={i.id} value={i.id}>
                            {i.desc}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid" id='modal-question-probability-error'>
                        {errors.probability?.message}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group id='modal-question-impact-group'>
                      <Form.Label id='modal-question-impact-label'>Impacto</Form.Label>
                      <Form.Select
                        id='modal-question-impact-select'
                        {...register('impact', {
                          required: 'O impacto caso essa questão ocorra, deve ser selecionado',
                          valueAsNumber: true,
                          validate: (value) => {
                            if (!value || value === 0) {
                              return 'O impacto caso essa questão ocorra, deve ser selecionado';
                            }
                            return true;
                          }
                        })}
                        isInvalid={!!errors.impact}
                      >
                        <option value={0}>Selecione o impacto</option>
                        {impactsList.map((i) => (
                          <option key={i.id} value={i.id}>
                            {i.desc}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid" id='modal-question-impact-error'>
                        {errors.impact?.message}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>
                <aside id='modal-question-risk-matrix'>
                  <Form.Label id='modal-question-risk-matrix-label'>Matriz de risco</Form.Label>
                  <span id='modal-question-risk-matrix-value'>{riskMatrixDesc || '-'}</span>
                </aside>
              </StyledRiskFactor>
            </Form.Group>            
          </StyledModalBody>
          <StyledModalFooter id='modal-question-footer' className='justify-content-between'>
            <Button 
              id='modal-question-cancel-button'
              type='button' 
              variant='light' 
              onClick={handleToggleModal}
            >
              Cancelar
            </Button>
            <SubmitButton 
              id='modal-question-save-button'
              type='submit' 
              loading={requesting} 
              block={false}
            >
              Salvar
            </SubmitButton>
          </StyledModalFooter>
        </form>
      </StyledModal>

      {showModalServiceDeskOpen && (
        <ModalServiceDesk
          modalData={showModalData}
          serviceDeskSugestionDesc={serviceDeskSugestionDesc}
        />
      )}
    </>
  );
};

export default ModalQuestion;
